import { useEffect, useRef } from "react";
import { ceremonyApi, type PendingCeremony } from "@/api/ceremony.api";
import { useCeremony } from "@/context/CeremonyContext";
import { useAuth } from "@/context/AuthContext";

const POLL_INTERVAL_MS = 30_000; // 30 seconds

/**
 * Polls for pending server-triggered ceremonies and fires them via CeremonyContext.
 * Should be mounted once in the app layout (inside AuthProvider + CeremonyProvider).
 */
export function useCeremonyPoller(): void {
  const { user } = useAuth();
  const { showCeremony } = useCeremony();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const processingRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    async function poll() {
      if (processingRef.current) return;
      processingRef.current = true;

      try {
        const pending = await ceremonyApi.getPending();
        for (const ceremony of pending) {
          showCeremony(
            ceremony.tier,
            ceremony.title,
            ceremony.message,
            buildExtras(ceremony)
          );
          // Mark as shown so it won't be returned again
          try {
            await ceremonyApi.markSeen(ceremony.ceremony_key);
          } catch {
            // Best effort — will be retried next poll if it fails
          }
        }
      } catch {
        // Silently ignore poll failures — will retry on next interval
      } finally {
        processingRef.current = false;
      }
    }

    // Initial poll after a short delay (let the page settle)
    const initialTimer = setTimeout(poll, 2000);

    // Recurring poll
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, showCeremony]);
}

function buildExtras(ceremony: PendingCeremony) {
  const meta = ceremony.metadata ?? {};
  return {
    xp: meta.xp as number | undefined,
    sp: meta.sp as number | undefined,
    levelName: meta.levelName as string | undefined,
    frameColor: meta.frameColor as string | undefined,
    showName: meta.showName as string | undefined,
    ceremonyKey: ceremony.ceremony_key,
    showId: meta.showId as number | undefined,
    goldStarNumber: meta.goldStarNumber as number | undefined,
    totalStarlets: meta.totalStarlets as number | undefined,
    badgeName: meta.badgeName as string | undefined,
    badgeSlug: meta.badgeSlug as string | undefined,
  };
}
